import { LENS_COLORS, lensColorForKey } from "../../catalog/lensColors";
import type { ProductFamily } from "../../catalog/taxonomy";
import { parseMoneyToMinor } from "../money";

/**
 * What the shop needs to know about a product that is not a pair of glasses.
 *
 * Ordinary commerce, and nothing else. There is no frame shape, no audience and
 * no try-on configuration here, because none of them describe a lens or a case
 * — and a cosmetic contact lens has no prescription fields either. These lenses
 * are a colour choice, not a medical device: no OD or OS, no sphere, power,
 * cylinder, axis or ADD, and nothing to upload or verify. Every field below is
 * one the backend actually stores.
 */

export interface NonGlassesDetails {
  name: string;
  productCode: string;
  description: string;
  /** Text as typed; converted to minor units only when it parses. */
  basePrice: string;
  salePrice: string;
  /** The approved lens colour key. Contact Lenses only. */
  lensColorKey: string | null;
  /** Stock for a product with one structural variant. Accessories, and lenses. */
  stock: string;
  /** Real, user-facing variants. Accessories only, and only when the product
   *  genuinely has a choice — see AccessoryVariants below. */
  variants: AccessoryVariant[];
}

export interface AccessoryVariant {
  name: string;
  swatch_hex: string;
  stock: string;
}

export function emptyNonGlassesDetails(): NonGlassesDetails {
  return {
    name: "",
    productCode: "",
    description: "",
    basePrice: "",
    salePrice: "",
    lensColorKey: null,
    stock: "0",
    variants: [],
  };
}

export function parseStock(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number.parseInt(trimmed, 10);
  return Number.isSafeInteger(value) ? value : null;
}

/**
 * Everything still unanswered, in the operator's words.
 *
 * Reported as a list rather than by throwing at the first one, so an
 * administrator fixes a form once instead of discovering its problems one
 * submission at a time.
 */
export function nonGlassesProblems(
  details: NonGlassesDetails,
  family: ProductFamily,
): string[] {
  const problems: string[] = [];

  if (!details.name.trim()) problems.push("The product needs a name.");
  if (!details.productCode.trim()) {
    problems.push("The product code is required before publishing.");
  }

  const base = parseMoneyToMinor(details.basePrice);
  if (base === null) problems.push("Enter a price, such as 120.00.");
  const sale = details.salePrice.trim()
    ? parseMoneyToMinor(details.salePrice)
    : null;
  if (details.salePrice.trim() && sale === null) {
    problems.push("The sale price is not an amount, such as 99.00.");
  }
  if (base !== null && sale !== null && sale > base) {
    problems.push("The sale price cannot be more than the price.");
  }

  if (family === "contact_lenses") {
    if (!lensColorForKey(details.lensColorKey)) {
      problems.push("Choose the lens colour.");
    }
  }

  if (details.variants.length > 0) {
    details.variants.forEach((variant, index) => {
      if (!variant.name.trim()) {
        problems.push(`Variant ${index + 1} needs a name.`);
      }
      if (parseStock(variant.stock) === null) {
        problems.push(`Variant ${index + 1} needs a stock quantity.`);
      }
    });
  } else if (parseStock(details.stock) === null) {
    problems.push("Enter the stock quantity as a whole number.");
  }

  return problems;
}

export function NonGlassesDetailsStep({
  family,
  details,
  problems,
  showProblems,
  onChange,
}: {
  family: Exclude<ProductFamily, "glasses">;
  details: NonGlassesDetails;
  problems: string[];
  showProblems: boolean;
  onChange: (next: NonGlassesDetails) => void;
}) {
  const set = <K extends keyof NonGlassesDetails>(
    key: K,
    value: NonGlassesDetails[K],
  ) => onChange({ ...details, [key]: value });

  return (
    <section className="step" aria-label="Product information">
      <div className="form-grid">
        <div>
          <label htmlFor="ng-name">Name</label>
          <input
            id="ng-name"
            value={details.name}
            aria-label="Name"
            onChange={(e) => set("name", e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="ng-code">Product code</label>
          <input
            id="ng-code"
            value={details.productCode}
            aria-label="Product code"
            onChange={(e) => set("productCode", e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="ng-price">Price</label>
          <input
            id="ng-price"
            value={details.basePrice}
            inputMode="decimal"
            aria-label="Price"
            onChange={(e) => set("basePrice", e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="ng-sale">Sale price</label>
          <input
            id="ng-sale"
            value={details.salePrice}
            inputMode="decimal"
            placeholder="No sale price"
            aria-label="Sale price"
            onChange={(e) => set("salePrice", e.target.value)}
          />
        </div>

        <div className="form-grid__full">
          <label htmlFor="ng-description">Description</label>
          <textarea
            id="ng-description"
            value={details.description}
            aria-label="Description"
            onChange={(e) => set("description", e.target.value)}
          />
        </div>
      </div>

      {family === "contact_lenses" ? (
        <LensColorChoice
          value={details.lensColorKey}
          onChange={(lensColorKey) => set("lensColorKey", lensColorKey)}
        />
      ) : null}

      {family === "accessories" ? (
        <AccessoryVariants
          variants={details.variants}
          onChange={(variants) => set("variants", variants)}
        />
      ) : null}

      {details.variants.length === 0 ? (
        <div className="field">
          <label htmlFor="ng-stock">Stock</label>
          <input
            id="ng-stock"
            className="num"
            type="number"
            min={0}
            value={details.stock}
            aria-label="Stock"
            style={{ width: 140 }}
            onChange={(e) => set("stock", e.target.value)}
          />
        </div>
      ) : null}

      {showProblems && problems.length > 0 ? (
        <div className="notice notice--warning" role="status" data-testid="details-problems">
          <ul>
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

/**
 * The lens colour, as six choices.
 *
 * One product is one colour: Ash Grey and Hazel are two products, not two
 * settings of one, which is why this is a single-answer control and not a
 * multi-select. The list comes from the one owner in catalog/lensColors.ts —
 * nothing here restates a key, a name or a swatch.
 */
function LensColorChoice({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (key: string) => void;
}) {
  return (
    <fieldset className="field">
      <legend className="field__label">Lens colour</legend>
      <p className="field__hint">
        One colour per product — this is what a customer picks it by.
      </p>
      <div className="choiceRow">
        {LENS_COLORS.map((color) => (
          <label
            key={color.key}
            className={value === color.key ? "choice is-selected" : "choice"}
          >
            <input
              type="radio"
              name="lens-color"
              value={color.key}
              checked={value === color.key}
              onChange={() => onChange(color.key)}
            />
            <span
              className="swatch"
              style={{ background: color.swatch_hex }}
              aria-hidden="true"
            />
            <span className="choice__label">{color.name}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Real variants, offered only if the accessory genuinely has them.
 *
 * An accessory that comes one way has no choice to present, and the single
 * variant the backend keeps its stock on is a structural fact, not a decision
 * anyone made. Showing that row as "Color: Default" would invent a colour the
 * shop does not sell and put it in front of an administrator as if it were
 * theirs to name — so by default there is no variant editor at all, only a
 * stock box, and this appears once the administrator says there is a choice.
 */
function AccessoryVariants({
  variants,
  onChange,
}: {
  variants: AccessoryVariant[];
  onChange: (next: AccessoryVariant[]) => void;
}) {
  const has = variants.length > 0;

  return (
    <div className="field">
      <label className="row" style={{ marginBottom: 0 }}>
        <input
          type="checkbox"
          style={{ width: "auto" }}
          checked={has}
          aria-label="This accessory comes in more than one variant"
          onChange={(event) =>
            onChange(
              event.target.checked
                ? [
                    { name: "", swatch_hex: "#000000", stock: "0" },
                    { name: "", swatch_hex: "#000000", stock: "0" },
                  ]
                : [],
            )
          }
        />
        <span>This accessory comes in more than one variant</span>
      </label>
      <p className="field__hint">
        Leave this off for an accessory that comes one way. Its stock is on the
        product itself, and customers are not asked to choose anything.
      </p>

      {has ? (
        <table className="table" data-testid="accessory-variants">
          <thead>
            <tr>
              <th>Name</th>
              <th style={{ width: 140 }}>Swatch</th>
              <th style={{ width: 120 }}>Stock</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {variants.map((variant, index) => (
              <tr key={index}>
                <td>
                  <input
                    value={variant.name}
                    aria-label={`Variant ${index + 1} name`}
                    onChange={(e) => {
                      const next = [...variants];
                      next[index] = { ...variant, name: e.target.value };
                      onChange(next);
                    }}
                  />
                </td>
                <td>
                  <input
                    value={variant.swatch_hex}
                    aria-label={`Variant ${index + 1} swatch`}
                    onChange={(e) => {
                      const next = [...variants];
                      next[index] = { ...variant, swatch_hex: e.target.value };
                      onChange(next);
                    }}
                  />
                </td>
                <td>
                  <input
                    className="num"
                    type="number"
                    min={0}
                    value={variant.stock}
                    aria-label={`Variant ${index + 1} stock`}
                    onChange={(e) => {
                      const next = [...variants];
                      next[index] = { ...variant, stock: e.target.value };
                      onChange(next);
                    }}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn--sm btn--subtle"
                    disabled={variants.length <= 1}
                    onClick={() => onChange(variants.filter((_, i) => i !== index))}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {has ? (
        <button
          type="button"
          className="btn btn--sm"
          onClick={() =>
            onChange([...variants, { name: "", swatch_hex: "#000000", stock: "0" }])
          }
        >
          Add another variant
        </button>
      ) : null}
    </div>
  );
}
